/**
 * Algolia API – all index/delete and search go through Cloud Functions.
 * No Algolia write or search keys on the frontend; keys stay in the backend.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';

// Use us-central1 to match deployed Cloud Functions and emulator (see firebase/emulator.js).
const functions = getFunctions(app, 'us-central1');

function call(name, data) {
  const fn = httpsCallable(functions, name);
  return fn(data);
}

/** Index one user. Payload: { objectID, name, email, phone, groupIds }. */
export async function userIndex(payload) {
  const result = await call('algoliaUserIndex', { payload });
  return result.data;
}

/** Remove user from Algolia index. */
export async function userDelete(objectID) {
  const result = await call('algoliaUserDelete', { objectID });
  return result.data;
}

/** Search teammates by name, same group. Returns hits (no email/phone). */
export async function searchTeammates(searcherGroupIds, query, options = {}) {
  const result = await call('algoliaSearchTeammates', { searcherGroupIds, query, options });
  return (result.data && result.data.hits) || [];
}

/** Find user by email or phone. Returns hits with name, email, phone. */
export async function findUserByEmailOrPhone(query, options = {}) {
  const result = await call('algoliaFindUserByContact', { query, options });
  return (result.data && result.data.hits) || [];
}

/** Index one venue. Payload: full Algolia record (objectID, name, address, pincode, city, area, courts, gamesSupported, etc.). */
export async function venueIndex(payload) {
  const result = await call('algoliaVenueIndex', { payload });
  return result.data;
}

/** Remove venue from Algolia index. */
export async function venueDelete(objectID) {
  const result = await call('algoliaVenueDelete', { objectID });
  return result.data;
}

/** Search venues. Returns { hits, queryID }. */
export async function searchVenues(query, options = {}) {
  const result = await call('algoliaSearchVenues', { query, options });
  return result.data || { hits: [], queryID: null };
}

/** Proxy: send click event (venue clicked from search). Backend calls Algolia Insights. */
export async function insightClick(params) {
  await call('algoliaInsightClick', params);
}

/** Proxy: send conversion event (e.g. court booked). Backend calls Algolia Insights. */
export async function insightConversion(params) {
  await call('algoliaInsightConversion', params);
}

/** Proxy: send view event (venue viewed). Backend calls Algolia Insights. */
export async function insightView(params) {
  await call('algoliaInsightView', params);
}
